import { joinPresent, initialsFromName } from '../mentorDashboardFormat';
import type { MenteeMentorProfilePayload } from './MenteePortalService';

type Props = {
    identity: MenteeMentorProfilePayload['identity'];
    tracks: MenteeMentorProfilePayload['careerTracks'];
    onSendMessage: () => void;
};

const firstNameOf = (name: string | null): string => {
    const trimmed = (name || '').trim();
    if (!trimmed) return '';
    return trimmed.split(/\s+/)[0];
};

const shortLinkedin = (url: string): string => url.replace(/^https?:\/\//i, '').replace(/\/$/, '');

/**
 * Mentor summary card. Replaces the old Basic Details table: the same identity fields are
 * rendered in the card layout, driven entirely by the payload — no placeholder content.
 */
const MenteeMyMentorBasicDetails = ({ identity, tracks, onSendMessage }: Props) => {
    const name = (identity.name || '').trim();
    const firstName = firstNameOf(identity.name);
    const roleLine = joinPresent([identity.designation, identity.company], ' · ');

    const study = joinPresent(
        [identity.programName, identity.batchYear != null ? String(identity.batchYear) : null],
        ' '
    );
    const hasExperience = identity.experienceYears != null && identity.experienceYears > 0;

    const trackNames = (tracks || [])
        .map((track) => (track.name || '').trim())
        .filter((value) => value !== '');

    if (!name && !roleLine && !study && !identity.location && !hasExperience && trackNames.length === 0) {
        return (
            <section className="mmentor-card">
                <h2 className="mmentor-card-title">Basic Details</h2>
                <p className="mmentor-empty">No profile details available yet.</p>
            </section>
        );
    }

    return (
        <div className="card border-0 shadow-sm rounded-3 p-4 mb-3 bg-white">
            <div className="row align-items-center g-3">
                <div className="col-auto">
                    <svg
                        width="72"
                        height="72"
                        className="rounded-circle border border-primary border-opacity-25 shadow-sm"
                        role="img"
                        aria-label={name || 'Mentor'}>
                        <rect width="100%" height="100%" fill="#EBF0FF"></rect>
                        <text
                            x="50%"
                            y="55%"
                            dominantBaseline="middle"
                            textAnchor="middle"
                            fill="#3557CC"
                            fontSize="20"
                            fontWeight="bold"
                            fontFamily="Outfit">
                            {initialsFromName(name)}
                        </text>
                    </svg>
                </div>

                <div className="col">
                    <h4 className="fw-bold mb-0 text-dark font-18">{name || 'Your mentor'}</h4>
                    {roleLine ? (
                        <div className="small fw-semibold text-secondary mt-1">{roleLine}</div>
                    ) : null}

                    {identity.location || study || hasExperience ? (
                        <div className="small text-muted mt-1 d-flex flex-wrap align-items-center gap-2">
                            {identity.location ? (
                                <span>
                                    <i className="bi bi-geo-alt-fill"></i> {identity.location}
                                </span>
                            ) : null}
                            {study ? (
                                <span>
                                    <i className="bi bi-mortarboard-fill"></i> {study}
                                </span>
                            ) : null}
                            {hasExperience ? (
                                <span>
                                    <i className="bi bi-briefcase-fill"></i> {identity.experienceYears} years of
                                    experience
                                </span>
                            ) : null}
                        </div>
                    ) : null}

                    {trackNames.length > 0 || identity.batchYear != null || identity.linkedinUrl ? (
                        <div className="d-flex flex-wrap gap-1 mt-2">
                            {trackNames.map((track) => (
                                <span
                                    key={track}
                                    className="badge bg-primary bg-opacity-10 text-primary font-12 font-500 py-1 px-2 border border-primary border-opacity-25">
                                    {track}
                                </span>
                            ))}
                            {identity.batchYear != null ? (
                                <span className="badge bg-success bg-opacity-10 text-success font-12 font-500 py-1 px-2 border border-success border-opacity-25">
                                    {joinPresent([identity.programName, String(identity.batchYear)], ' ')} Batch
                                </span>
                            ) : null}
                            {identity.linkedinUrl ? (
                                <a
                                    href={identity.linkedinUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="badge bg-secondary bg-opacity-10 text-secondary font-12 font-500 py-1 px-2 border border-secondary border-opacity-25 text-decoration-none">
                                    {shortLinkedin(identity.linkedinUrl)}
                                </a>
                            ) : null}
                        </div>
                    ) : null}
                </div>

                <div className="col-lg-auto mt-3 mt-lg-0 d-flex flex-column gap-2">
                    <button type="button" className="btn btn-outline-success fw-medium" onClick={onSendMessage}>
                        <i className="bi bi-chat-fill"></i> {firstName ? `Message ${firstName}` : 'Send Message'}
                    </button>
                    <button type="button" className="btn btn-green" disabled title="Booking arrives with Sessions">
                        <span>
                            <i className="bi bi-calendar-check-fill"></i> Book a Session
                        </span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MenteeMyMentorBasicDetails;
